import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Inbox, ArrowLeft, Lock, LogOut, Loader2, Sparkles, Copy, Send } from "lucide-react";
import { api } from "../lib/api";

const TOKEN_KEY = "jx_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/enquiries", { headers: { Authorization: `Bearer ${token}` } });
      setEnquiries(data);
      const subsRes = await api.get("/subscribers", { headers: { Authorization: `Bearer ${token}` } });
      setSubs(subsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError("Could not load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(); else setLoading(false); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries([]);
  };

  const [drafts, setDrafts] = useState({});
  const [subs, setSubs] = useState([]);
  const draftReply = async (id) => {
    setDrafts((d) => ({ ...d, [id]: { status: "loading" } }));
    try {
      const { data } = await api.post(`/enquiries/${id}/draft-reply`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setDrafts((d) => ({ ...d, [id]: { status: "done", text: data.draft } }));
    } catch (err) {
      setDrafts((d) => ({ ...d, [id]: { status: "error", text: err?.response?.status === 401 ? "Session expired — sign in again." : "Draft generation failed. Try again." } }));
      if (err?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(""); }
    }
  };

  const sendReply = async (id) => {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], sending: true, sendError: null } }));
    try {
      await api.post(`/enquiries/${id}/send-reply`, { body: drafts[id].text }, { headers: { Authorization: `Bearer ${token}` } });
      setDrafts((d) => ({ ...d, [id]: { ...d[id], sending: false, sent: true } }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setDrafts((d) => ({ ...d, [id]: { ...d[id], sending: false, sendError: typeof detail === "string" ? detail : "Send failed — the address may not accept email." } }));
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-jx-night flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm rounded-3xl bg-white p-10">
          <div className="w-12 h-12 rounded-2xl jx-gradient-bg flex items-center justify-center">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <img src="/assets/logo-horizontal-sm.png" alt="JIYONOX" className="mt-6 h-8 w-auto" />
          <h1 className="mt-4 font-display font-extrabold text-2xl tracking-tighter text-jx-ink">Admin sign in</h1>
          <p className="mt-1 text-sm text-jx-ink/50">Private area — JIYONOX team only.</p>
          <input required type="email" data-testid="admin-login-email" value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="mt-6 w-full bg-transparent border-b border-jx-ink/15 focus:border-jx-purple outline-none py-3 text-base text-jx-ink placeholder:text-jx-ink/35 transition-colors" />
          <input required type="password" data-testid="admin-login-password" value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" className="mt-4 w-full bg-transparent border-b border-jx-ink/15 focus:border-jx-purple outline-none py-3 text-base text-jx-ink placeholder:text-jx-ink/35 transition-colors" />
          {loginError && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600">{loginError}</p>}
          <button type="submit" disabled={loggingIn} data-testid="admin-login-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full jx-gradient-bg text-white font-medium px-6 py-3.5 disabled:opacity-60 transition-opacity">
            {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </button>
          <a href="/" className="mt-5 inline-flex items-center gap-2 text-sm text-jx-ink/50 hover:text-jx-purple transition-colors"><ArrowLeft className="w-4 h-4" /> Back to site</a>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-jx-paper">
      <header className="border-b border-jx-ink/8 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="admin-logo" className="flex items-center"><img src="/assets/logo-horizontal-sm.png" alt="JIYONOX" className="h-8 w-auto" /></a>
          <div className="flex items-center gap-4">
            <button data-testid="admin-refresh-button" onClick={() => load()} className="inline-flex items-center gap-2 text-sm font-medium text-jx-ink/60 hover:text-jx-ink transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm font-medium text-jx-ink/60 hover:text-jx-magenta transition-colors">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
            <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-jx-purple hover:text-jx-magenta transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to site
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="font-display font-extrabold text-3xl tracking-tighter text-jx-ink">Enquiries</h1>
        <p className="mt-2 text-sm text-jx-ink/50" data-testid="admin-count">{enquiries.length} conversation{enquiries.length === 1 ? "" : "s"} received</p>

        {error && <p data-testid="admin-error" className="mt-6 text-sm text-red-600">{error}</p>}

        {!loading && enquiries.length === 0 && !error && (
          <div data-testid="admin-empty-state" className="mt-16 text-center">
            <Inbox className="w-10 h-10 text-jx-ink/20 mx-auto" />
            <p className="mt-4 text-jx-ink/50">No enquiries yet. They will appear here when someone starts a conversation.</p>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-4">
          {enquiries.map((e, i) => (
            <article key={e.id} data-testid={`admin-enquiry-${i}`} className="rounded-2xl bg-white border border-jx-ink/8 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display font-bold text-lg text-jx-ink">{e.name}</p>
                  <p className="text-sm text-jx-ink/50">{e.email}{e.company ? ` · ${e.company}` : ""}</p>
                </div>
                <div className="text-right">
                  {e.interest && <span className="inline-block rounded-full bg-jx-mist px-3 py-1 text-[11px] font-semibold text-jx-purple">{e.interest}</span>}
                  <p className="mt-1 text-xs text-jx-ink/40">{new Date(e.created_at).toLocaleString()}</p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-jx-ink/70 whitespace-pre-wrap">{e.message}</p>
              <div className="mt-4 flex items-center gap-3">
                <button
                  data-testid={`draft-reply-${i}`}
                  onClick={() => draftReply(e.id)}
                  disabled={drafts[e.id]?.status === "loading"}
                  className="inline-flex items-center gap-2 rounded-full border border-jx-purple/25 text-jx-purple text-xs font-bold tracking-wide px-4 py-2 hover:bg-jx-purple hover:text-white transition-[background-color,color] duration-300 disabled:opacity-50"
                >
                  {drafts[e.id]?.status === "loading" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {drafts[e.id]?.status === "loading" ? "Drafting..." : "Draft AI Reply"}
                </button>
                <a href={`mailto:${e.email}`} data-testid={`reply-mailto-${i}`} className="text-xs font-medium text-jx-ink/45 hover:text-jx-magenta transition-colors">
                  Reply by email →
                </a>
              </div>
              {drafts[e.id]?.status === "done" && (
                <div data-testid={`draft-text-${i}`} className="mt-3 rounded-xl bg-jx-mist border border-jx-purple/15 p-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-jx-purple">AI Draft — edit, then send</p>
                    <button
                      data-testid={`draft-copy-${i}`}
                      onClick={() => navigator.clipboard.writeText(drafts[e.id].text)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-jx-ink/50 hover:text-jx-purple transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </button>
                  </div>
                  <textarea
                    data-testid={`draft-edit-${i}`}
                    value={drafts[e.id].text}
                    onChange={(ev) => setDrafts((d) => ({ ...d, [e.id]: { ...d[e.id], text: ev.target.value, sent: false } }))}
                    rows={9}
                    className="w-full bg-white rounded-lg border border-jx-ink/10 p-4 text-sm leading-relaxed text-jx-ink/80 outline-none focus:border-jx-purple transition-colors resize-y"
                  />
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <button
                      data-testid={`draft-send-${i}`}
                      onClick={() => sendReply(e.id)}
                      disabled={drafts[e.id].sending}
                      className="inline-flex items-center gap-2 rounded-full jx-gradient-bg text-white text-xs font-bold tracking-wide px-5 py-2.5 transition-transform duration-300 hover:scale-[1.03] active:scale-95 disabled:opacity-50"
                    >
                      {drafts[e.id].sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      {drafts[e.id].sending ? "Sending..." : `Send to ${e.email}`}
                    </button>
                    {drafts[e.id].sent && <span data-testid={`draft-sent-${i}`} className="text-xs font-semibold text-green-600">Sent successfully</span>}
                    {drafts[e.id].sendError && <span data-testid={`draft-send-error-${i}`} className="text-xs text-red-600">{drafts[e.id].sendError}</span>}
                  </div>
                </div>
              )}
              {drafts[e.id]?.status === "error" && <p data-testid={`draft-error-${i}`} className="mt-3 text-xs text-red-600">{drafts[e.id].text}</p>}
            </article>
          ))}
        </div>

        <div className="mt-16" data-testid="subscribers-section">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-display font-extrabold text-2xl tracking-tighter text-jx-ink">Newsletter Subscribers</h2>
              <p className="mt-1 text-sm text-jx-ink/50" data-testid="subscribers-count">{subs.length} subscriber{subs.length === 1 ? "" : "s"} from the Insights section</p>
            </div>
          </div>
          <div className="mt-5 flex flex-col gap-2">
            {subs.map((s, i) => (
              <div key={s.email} data-testid={`subscriber-${i}`} className="flex items-center justify-between rounded-xl bg-white border border-jx-ink/8 px-5 py-3.5">
                <span className="text-sm font-medium text-jx-ink/80">{s.email}</span>
                <span className="text-xs text-jx-ink/40" data-testid={`subscriber-date-${i}`}>
                  {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
                </span>
              </div>
            ))}
            {subs.length === 0 && <p className="text-sm text-jx-ink/45 py-4">No subscribers yet — they'll appear here when someone joins from the Insights section.</p>}
          </div>
        </div>
      </main>
    </div>
  );
}
